import { useEffect, useMemo, useRef, useState } from "react";
import { SYMBOL_SWEEP_URL } from "../../config/api";
import { authHeader } from "../../utils/apiToken";
import { entityKeyOf } from "../../utils/overlapCheck";
import {
  SCOPES, resolveScope, runCountByExample, buildResultRows, markAlreadyPlaced, placementsFrom, summarizeRows,
} from "../../utils/countByExample";

// Count by example: the person boxes ONE example symbol on a drawing (the canvas's marquee), chooses what it is
// and WHICH drawings to search (this one, chosen ones, or all), reviews every find, and only then places the
// ticked ones — one undo step. The reading of the lines happens on the server (cs-est-takeoff-api/symbol-sweep);
// this panel only orchestrates and presents. Nothing is placed until "Place".

const sheetLabel = (s) => s.SheetNumber ? `${s.SheetNumber}${s.SheetName ? ` — ${s.SheetName}` : ""}` : (s.SheetName || `Drawing ${s.SheetID}`);
const typeKeyToEntityKey = (k) => (k?.startsWith("asm:") ? `assembly:${k.slice(4)}` : k?.startsWith("item:") ? k : null);

async function post(body) {
  const res = await fetch(SYMBOL_SWEEP_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`The search service answered HTTP ${res.status}.`);
  return res.json();
}

export default function CountByExamplePanel({
  revisionId, sheets, activeSheetId, seed, conditionTypes, conditions, tunables, isLocked,
  onMarkers, onGo, onApply, onPickAgain, onClose,
}) {
  const [example, setExample] = useState({ status: "reading", message: "", fingerprint: null }); // reading | ok | error
  const [typeKey, setTypeKey] = useState("");
  const [scope, setScope] = useState(SCOPES.current);
  const [chosen, setChosen] = useState(() => new Set());
  const [phase, setPhase] = useState("setup"); // setup | running | results
  const [progress, setProgress] = useState(null);
  const [sheetResults, setSheetResults] = useState([]);
  const [rows, setRows] = useState([]);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState("");
  const abortRef = useRef(null);

  const countTypes = useMemo(() => (conditionTypes || []).filter(t => t.Style === "count"), [conditionTypes]);
  const configured = !!SYMBOL_SWEEP_URL;
  const scopePlan = useMemo(() => resolveScope({ mode: scope, sheets, activeSheetId, selectedIds: [...chosen] }), [scope, sheets, activeSheetId, chosen]);
  const entityKey = typeKeyToEntityKey(typeKey);

  // Read the example whenever a new box is drawn.
  useEffect(() => {
    let stop = false;
    setExample({ status: "reading", message: "", fingerprint: null });
    setPhase("setup"); setRows([]); setSheetResults([]); setDone("");
    if (!configured) { setExample({ status: "error", message: "Count by example is not configured for this environment yet (VITE_SYMBOL_SWEEP_URL).", fingerprint: null }); return; }
    (async () => {
      try {
        const r = await post({ action: "seed", revision_id: revisionId, sheet_id: seed.sheetId, box: seed.box });
        if (stop) return;
        if (r.available === false) setExample({ status: "error", message: r.message || "This drawing cannot be searched.", fingerprint: null });
        else if (!r.ok) setExample({ status: "error", message: r.message || "Could not read an example from that box.", fingerprint: null });
        else setExample({ status: "ok", message: `${r.fingerprint.segs.length} lines read from the example.`, fingerprint: r.fingerprint });
      } catch (e) { if (!stop) setExample({ status: "error", message: e.message || "Could not read the example.", fingerprint: null }); }
    })();
    return () => { stop = true; };
  }, [seed, revisionId, configured]);

  // Markers on the drawing the person is looking at.
  useEffect(() => { onMarkers?.(rows); }, [rows, onMarkers]);
  useEffect(() => () => { onMarkers?.([]); abortRef.current?.abort(); }, [onMarkers]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function run() {
    if (!entityKey || !example.fingerprint || !scopePlan.targets.length) return;
    const ctl = new AbortController();
    abortRef.current = ctl;
    setPhase("running"); setRows([]); setDone("");
    const fp = example.fingerprint;
    const r = await runCountByExample({
      targets: scopePlan.targets, signal: ctl.signal, onProgress: setProgress,
      matchCall: (sheet) => post({ action: "match", revision_id: revisionId, sheet_id: sheet.SheetID, fingerprint: fp, example: { sheet_id: seed.sheetId, center: fp.center } }),
    });
    setSheetResults(r.sheets);
    setRows(markAlreadyPlaced(buildResultRows(r.sheets), conditions, entityKey, entityKeyOf, tunables));
    setPhase("results"); setProgress(null);
  }

  const summary = useMemo(() => summarizeRows(rows), [rows]);
  const toggle = (id) => setRows(rs => rs.map(r => (r.id === id && !r.alreadyPlaced ? { ...r, checked: !r.checked } : r)));
  const setAll = (fn) => setRows(rs => rs.map(r => (r.alreadyPlaced ? r : { ...r, checked: fn(r) })));

  async function apply() {
    const placements = placementsFrom(rows);
    if (!placements.length || applying) return;
    setApplying(true);
    try {
      const out = await onApply({ conditionTypeId: typeKey, placements });
      if (out?.placed) { setDone(`Placed ${out.placed} symbol${out.placed === 1 ? "" : "s"} on ${summary.sheets} drawing${summary.sheets === 1 ? "" : "s"}. Ctrl+Z undoes them all at once.`); setRows([]); setPhase("setup"); setSheetResults([]); }
    } finally { setApplying(false); }
  }

  const box = { position: "absolute", top: 8, right: 8, zIndex: 20, width: 380, maxHeight: "calc(100% - 16px)", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.25)", padding: 12, fontSize: 12 };
  const h = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text-muted)", margin: "10px 0 4px" };
  const btn = (primary, disabled) => ({ fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 6, border: `1px solid ${primary ? "#2563eb" : "var(--border)"}`, background: primary ? "#2563eb" : "var(--surface)", color: primary ? "#fff" : "var(--text)", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 });

  const rowsBySheet = new Map();
  for (const r of rows) { if (!rowsBySheet.has(r.sheetId)) rowsBySheet.set(r.sheetId, []); rowsBySheet.get(r.sheetId).push(r); }

  return (
    <div style={box} role="dialog" aria-label="Count by example">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>🎯 Count by example</div>
        <button onClick={onClose} style={{ ...btn(false), padding: "2px 8px" }} aria-label="Close">✕</button>
      </div>

      <div style={h}>1 · The example</div>
      <div style={{ color: example.status === "error" ? "#dc2626" : "var(--text-muted)" }}>
        {example.status === "reading" ? "Reading the example…" : example.message}
      </div>
      <button onClick={onPickAgain} style={{ ...btn(false), marginTop: 6 }}>Draw the box again</button>

      <div style={h}>2 · What is it?</div>
      <select value={typeKey} onChange={e => setTypeKey(e.target.value)} style={{ width: "100%", fontSize: 12, padding: 4 }} aria-label="Condition type to place">
        <option value="">Choose the condition to place…</option>
        {countTypes.map(t => <option key={t.TypeKey} value={t.TypeKey}>{t.Code ? `${t.Code} — ` : ""}{t.Name}</option>)}
      </select>
      {!countTypes.length && <div style={{ color: "var(--text-muted)", marginTop: 4 }}>No placeable (count) conditions are set up yet — add one on Setup ▸ Estimate Engine ▸ Items.</div>}

      <div style={h}>3 · Which drawings?</div>
      {[[SCOPES.current, "This drawing"], [SCOPES.selected, "Choose drawings…"], [SCOPES.all, "All drawings"]].map(([k, label]) => (
        <label key={k} style={{ display: "block", margin: "2px 0" }}>
          <input type="radio" name="sweep-scope" checked={scope === k} onChange={() => setScope(k)} /> {label}
        </label>
      ))}
      {scope === SCOPES.selected && (
        <div style={{ maxHeight: 130, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 6, padding: 6, marginTop: 4 }}>
          {sheets.map(s => {
            const why = resolveScope({ mode: SCOPES.selected, sheets, selectedIds: [s.SheetID] }).skipped[0]?.message;
            return (
              <label key={s.SheetID} style={{ display: "block", opacity: why ? 0.55 : 1 }} title={why || ""}>
                <input type="checkbox" disabled={!!why} checked={chosen.has(s.SheetID)} onChange={() => setChosen(c => { const n = new Set(c); if (n.has(s.SheetID)) n.delete(s.SheetID); else n.add(s.SheetID); return n; })} /> {sheetLabel(s)}{why ? ` — ${why}` : ""}
              </label>
            );
          })}
        </div>
      )}
      <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
        Will search {scopePlan.targets.length} drawing{scopePlan.targets.length === 1 ? "" : "s"}
        {scopePlan.skipped.length ? `; ${scopePlan.skipped.length} cannot be searched (${[...new Set(scopePlan.skipped.map(s => s.reason === "no_scale" ? "no scale" : "not a vector PDF"))].join(", ")})` : ""}.
      </div>

      <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={run} disabled={phase === "running" || example.status !== "ok" || !entityKey || !scopePlan.targets.length} style={btn(true, phase === "running" || example.status !== "ok" || !entityKey || !scopePlan.targets.length)}>Find matches</button>
        {phase === "running" && <button onClick={() => abortRef.current?.abort()} style={btn(false)}>Stop</button>}
        {phase === "running" && progress && <span style={{ color: "var(--text-muted)" }}>Searching {progress.index + 1} of {progress.total}…</span>}
      </div>
      {done && <div style={{ marginTop: 8, color: "#15803d", fontWeight: 600 }}>{done}</div>}

      {phase === "results" && (
        <>
          <div style={h}>4 · Review</div>
          <div style={{ marginBottom: 6 }}>
            {summary.total === 0 ? "No matches found." : `${summary.matches} match${summary.matches === 1 ? "" : "es"}${summary.near ? `, ${summary.near} near (partly matching — unticked)` : ""}${summary.alreadyPlaced ? `, ${summary.alreadyPlaced} already placed` : ""}.`}
          </div>
          {sheetResults.filter(sr => sr.status !== "done" || sr.capped || sr.truncated).map(sr => (
            <div key={sr.sheet.SheetID} style={{ color: sr.status === "done" ? "#b45309" : "#dc2626", marginBottom: 2 }}>
              {sheetLabel(sr.sheet)}: {sr.status !== "done" ? sr.message : `${sr.truncated ? "only part of this sheet's linework could be read. " : ""}${sr.capped ? "result limit reached — more matches may exist." : ""}`}
            </div>
          ))}
          {rows.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <button style={btn(false)} onClick={() => setAll(r => r.tier === "match")}>Select matches</button>
              <button style={btn(false)} onClick={() => setAll(() => false)}>Clear</button>
            </div>
          )}
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {[...rowsBySheet.entries()].map(([sid, list]) => (
              <div key={sid}>
                <div style={{ fontWeight: 600, margin: "4px 0 2px" }}>{list[0].sheetName} · {list.length}</div>
                {list.map((r, i) => (
                  <label key={r.id} style={{ display: "flex", gap: 6, alignItems: "center", padding: "1px 0", opacity: r.alreadyPlaced ? 0.55 : 1 }}>
                    <input type="checkbox" checked={r.checked} disabled={r.alreadyPlaced} onChange={() => toggle(r.id)} />
                    <span style={{ minWidth: 22, fontWeight: 600 }}>{i + 1}</span>
                    <span style={{ color: r.tier === "match" ? "#15803d" : "#b45309" }}>{Math.round(r.score * 100)}%</span>
                    <span style={{ color: "var(--text-muted)", flex: 1 }}>{r.isExample ? "your example" : r.alreadyPlaced ? "already placed" : r.tier === "near" ? "near match" : ""}</span>
                    <button style={{ ...btn(false), padding: "0 6px", fontSize: 11 }} onClick={(e) => { e.preventDefault(); onGo?.(r.sheetId, r.x, r.y); }} title="Go to it on the drawing">go</button>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10 }}>
            <button onClick={apply} disabled={isLocked || applying || summary.checked === 0} style={btn(true, isLocked || applying || summary.checked === 0)}>
              {applying ? "Placing…" : `Place ${summary.checked} symbol${summary.checked === 1 ? "" : "s"}`}
            </button>
            {isLocked && <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>This revision is locked.</span>}
          </div>
        </>
      )}
    </div>
  );
}
